import { Component, computed, input, ViewEncapsulation } from '@angular/core';

export type SkeletonBlockRadius = 'bar' | 'card' | 'shell' | 'stub' | 'circle';
export type SkeletonBlockTone = 'base' | 'secondary' | 'emphasis';

/**
 * Decorative skeleton placeholder with design-pack sweep animation and tones.
 * Encapsulation is disabled so `@keyframes` are not renamed and the fade/sweep runs in the browser.
 */
@Component({
  selector: 'app-skeleton-block',
  templateUrl: './skeleton-block.html',
  styleUrl: './skeleton-block.scss',
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'skeleton-block-host',
    'aria-hidden': 'true',
  },
})
export class SkeletonBlock {
  /** CSS width value, or a number treated as pixels. */
  readonly width = input<string | number>('100%');
  /** CSS height value, or a number treated as pixels. */
  readonly height = input<string | number>(12);
  /** Corner treatment: bar (6px), card (4px), shell (2px), stub (3px), or full circle. */
  readonly radius = input<SkeletonBlockRadius>('bar');
  /** Fill tone from the skeleton design pack. */
  readonly tone = input<SkeletonBlockTone>('base');
  /**
   * When false, uses a flat fill with no sweep (design-pack radio placeholders).
   */
  readonly animated = input(true);

  protected readonly widthStyle = computed(() => toCssSize(this.width()));
  protected readonly heightStyle = computed(() => toCssSize(this.height()));
}

/**
 * Normalizes sizes to CSS lengths, treating unitless values as pixels.
 * Static template attributes (`width="330"`) arrive as strings; without a unit the browser
 * discards the declaration and the block collapses to zero height.
 */
function toCssSize(value: string | number): string {
  if (typeof value === 'number') {
    return `${value}px`;
  }

  const trimmed = value.trim();
  // Linear pattern (integers or decimals) — avoid `\d*\.?` which Sonar flags for backtracking.
  return /^\d+(?:\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
}
